import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AnimationOptions } from 'ngx-lottie';
import { AppSettings } from 'src/app/app-settings';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { UserService } from 'src/app/services/user.service';
import { ReportsService } from 'src/app/services/reports.service';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { CardValidateComponent } from '../card-validate/card-validate.component';
import jwt_decode from "jwt-decode";
import { NavigationEnd, Router } from '@angular/router';
import { PermissionService } from 'src/app/services/permission.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { Subscription, filter } from 'rxjs';

interface MenuOption {
  label: string;
  icon: string;
  link: string;
  /** Clave de `counts` que se pinta como globo a la derecha, si la hay. */
  badge?: string;
}

interface MenuGroup {
  title: string;
  options: MenuOption[];
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, AfterViewInit, OnDestroy {

  isMobile: boolean = false;
  showMenu: boolean = true;
  /** Sin menú ni saludo: la pantalla de una entrada. */
  fullWidth: boolean = false;

  profile!: any;
  profileMe!: any;
  greeting: string = '¡Hola!';
  counts: any = {};
  urlImages: string = AppSettings.MEDIA_URI + '/users_images/';
  loadingLottie: AnimationOptions = {
    path: 'assets/lotties/loading.json',
  };

  /**
   * El menú, una sola vez. Escritorio y móvil pintan esta misma lista, así
   * que una opción nueva entra en los dos sitios a la vez.
   *
   * Fuera quedan, con su componente y su ruta comentados pero sin borrar:
   * métodos de pago (Redsys no llega a guardar tarjetas), mis intereses y
   * suscripciones (las pantallas no hacen nada) y descuentos (nunca tuvo
   * ruta). «Mis abonos» se queda siempre visible aunque el usuario no tenga
   * ninguno: es la forma de que se entere de que la opción existe.
   */
  menuGroups: MenuGroup[] = [
    {
      title: 'Mi cuenta',
      options: [
        { label: 'Mis entradas', icon: 'fa-ticket', link: '/profile/entradas', badge: 'upcoming' },
        { label: 'Mis abonos', icon: 'fa-id-card', link: '/profile/abonos' },
        { label: 'Mis datos', icon: 'fa-user', link: '/profile/mis-datos' },
      ]
    },
    {
      title: 'Ajustes',
      options: [
        { label: 'Notificaciones', icon: 'fa-bell', link: '/profile/notificaciones' },
        { label: 'Ayuda y FAQs', icon: 'fa-circle-question', link: '/profile/ayuda' },
      ]
    }
  ];

  profileAdmin: boolean = false;
  /**
   * Gestor (admin o superadmin): puede pasar al panel. Mismo criterio que la
   * cabecera y que la redirección del constructor.
   */
  esGestor: boolean = false;

  private routerSub?: Subscription;

  constructor(
    private users: UserService,
    private auth: AuthenticationService,
    private dialog: MatDialog,
    private notificationsService: NotificationsService,
    private reports: ReportsService,
    private router: Router,
    private permissionService: PermissionService,
    private apiBusiness: ApiBusinessService,
    private el: ElementRef
  ) {
    let profile = this.auth.getProfile();
    let isAdmin = profile?.roles?.filter((role: any) => role.name == 'admin' || role.name == 'superadmin').length > 0;
    this.esGestor = isAdmin;
    if (isAdmin && this.auth.getUserMode() === 'organizer') {
      this.router.navigate(['/panel-control/events']);
    }
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 700;
    this.updateMenuVisibility(this.router.url);
    this.getData();
    this.getCounts();

    // En móvil el menú y la sección son dos pantallas, así que hay que
    // recalcular cuál toca en cada navegación (también al darle a "atrás"
    // del navegador, que no pasa por backToMenu).
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => this.updateMenuVisibility(event.urlAfterRedirects));

    var decoded: any = jwt_decode(localStorage.getItem('jwt_token') || '');
    if (decoded.roles?.includes("admin") || decoded.roles?.includes("superadmin")) {
      this.profileAdmin = true;
    }

    if (!this.isMobile && this.router.url == '/profile') {
      this.router.navigate(['/profile/entradas']);
    }
  }

  ngAfterViewInit(): void {
    this.medirCabecera();
  }

  /**
   * Móvil o escritorio se decide también al cambiar de tamaño, no solo al
   * cargar: si no, al estrechar la ventana (o girar una tablet) se quedaba en
   * modo escritorio y en una sección salían el saludo, el menú entero y la
   * sección uno debajo de otro.
   */
  comprobarMovil() {
    const movil = window.innerWidth <= 700;
    if (movil === this.isMobile) {
      return;
    }

    this.isMobile = movil;
    this.updateMenuVisibility(this.router.url);

    // En escritorio /profile a secas no enseña nada: el menú va al lado.
    if (!movil && this.router.url === '/profile') {
      this.router.navigate(['/profile/entradas']);
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  /**
   * Dónde se para el menú fijo y cuánto puede llegar a medir.
   *
   * Dos medidas, y las dos hay que tomarlas del DOM porque ninguna es
   * constante:
   *
   *  - La cabecera es 80px, 60 en pantallas pequeñas, y encoge al hacer
   *    scroll. Clavar un número acaba siempre con el menú pegado al logo en
   *    alguna combinación.
   *
   *  - El pie (unos 320px) es el que da el problema de verdad. Un elemento
   *    `sticky` no puede salirse de su rejilla, y cuando llegas abajo del todo
   *    el pie ocupa la parte baja de la pantalla: la rejilla se acaba arriba y
   *    el menú se pega a su final, colándose por debajo de la cabecera. Pasa
   *    en cuanto el menú mide más de lo que queda entre la cabecera y el pie,
   *    o sea en pantallas de menos de ~860px de alto.
   *
   * Así que el menú se limita a ese hueco real y, si no le cabe, rueda por
   * dentro. Nunca se pinta por debajo de la cabecera.
   */
  /**
   * Un solo listener de `resize` para todo: con dos en el mismo componente
   * Angular compila solo uno y el otro se pierde sin avisar.
   */
  @HostListener('window:resize')
  alCambiarDeTamano() {
    this.comprobarMovil();
    this.medirCabecera();
  }

  @HostListener('window:scroll')
  medirCabecera() {
    const alto = (selector: string, porDefecto: number) => {
      const el = document.querySelector(selector) as HTMLElement | null;
      return el ? Math.round(el.getBoundingClientRect().height) : porDefecto;
    };

    const cabecera = alto('.header', 80);
    const pie = alto('app-footer', 320);
    const menu = alto('.rail', 0);
    const aire = 32;

    const top = cabecera + aire;
    // El hueco de verdad entre la cabecera y el pie cuando estás abajo del
    // todo. Si el menú no cabe ahí, dejarlo fijo solo consigue que se cuele
    // por debajo de la cabecera, así que en ventanas muy bajas se queda
    // quieto y rueda con la página, como cualquier otra cosa. Entero y sin
    // scroll propio, que es lo que se ve raro.
    const hueco = window.innerHeight - pie - top;

    this.el.nativeElement.style.setProperty('--rail-top', `${top}px`);
    // El alto de la cabecera a secas, para lo que se pega justo debajo de
    // ella (el "← Mis entradas" de la pantalla de una entrada).
    this.el.nativeElement.style.setProperty('--cabecera-alto', `${cabecera}px`);
    this.el.nativeElement.style.setProperty('--rail-position', menu > hueco ? 'static' : 'sticky');
  }

  updateMenuVisibility(url: string) {
    // La pantalla de una entrada va a ancho completo, sin menú ni saludo,
    // como en el diseño: es lo que se enseña en la puerta y el menú solo le
    // quitaba sitio al pase y al cartel. Se vuelve por "← Mis entradas".
    this.fullWidth = /^\/profile\/entradas\/\d+/.test(url);
    this.showMenu = this.fullWidth ? false : (this.isMobile ? url === '/profile' : true);
  }

  backToMenu() {
    this.router.navigate(['/profile']);
  }

  getData() {
    this.auth.getUsersMe().subscribe({
      next: (res) => {
        if (res && res.data) {
          let data = res.data;
          let user = data.user || data;
          let profileData = { ...user, roles: data.roles, companies: data.companies };
          this.profileMe = profileData;
          this.profile = profileData;
          this.auth.setProfile(profileData);
          // Otra vez aquí: al cargar en frío el constructor aún no tiene el
          // perfil y no sabría que es gestor.
          this.esGestor = (profileData.roles || []).some((role: any) => role.name == 'admin' || role.name == 'superadmin');
        }
        this.setGreeting();
      },
      error: () => {
        this.profileMe = this.auth.getProfile();
        this.profile = this.auth.getProfile();
        this.setGreeting();
      }
    });
  }

  /**
   * Solo el nombre de pila: el apellido no aporta nada en un saludo y hace
   * que se corte en móvil.
   */
  setGreeting() {
    const name = (this.profileMe?.name || '').trim().split(' ')[0];
    this.greeting = name ? `¡Hola ${name}!` : '¡Hola!';
  }

  /**
   * El globo de "Mis entradas". Se pide con la página más pequeña posible
   * porque lo único que interesa es el contador.
   */
  getCounts() {
    this.reports.getOrdersByUser(1, 1, { scope: 'upcoming' }).subscribe({
      next: (res) => {
        this.counts = res?.data?.counts || {};
      },
      error: () => {
        this.counts = {};
      }
    });
  }

  deleteAccount() {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: {
        icon: true,
        text: '¿Estás seguro de que deseas eliminar tu cuenta? Todos tus datos se perderán.',
        btnCancel: true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialog.closeAll();
      }
    })
  }

  logout() {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: {
        icon: true,
        text: '¿Estás seguro de que quieres cerrar sesión?',
        btnCancel: true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.auth.logout();
      }
    })
  }

  openInfoSave() {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: false,
        text: 'Se han guardado los cambios correctamente',
        btnCancel: false
      }
    });
  }

  openValidate() {
    const dialogRef = this.dialog.open(CardValidateComponent, {
      panelClass: 'info-dialog'
    });
    dialogRef.afterClosed().subscribe({
      next: res => {
        if (res && res == 'validate') {
          const dialogConfirm = this.dialog.open(InfoCardComponent, {
            panelClass: 'info-dialog',
            data: {
              icon: false,
              text: 'El teléfono se ha verificado correctamente. ¡Ya puedes empezar a subir tus loks!',
              btnCancel: false
            }
          });
          dialogConfirm.afterClosed().subscribe(() => location.reload());
        }
      }
    })
  }

  openAdmin() {
    this.router.navigate(['/admin'])
  }

  /**
   * Al panel, en modo gestor: lo mismo que el selector "Usuario / Gestor" de
   * la cabecera, que por debajo de 1200px no se ve (va en la parte que
   * Bootstrap pliega). Sin esto, un gestor en el móvil no tenía cómo volver
   * al panel desde su cuenta. Si aún no hay empresa elegida, la primera.
   */
  irAlPanel() {
    const business = JSON.parse(localStorage.getItem('business') || '{}');
    if (!business?.id && this.profileMe?.roleFunctions?.length > 0) {
      this.apiBusiness.setBusinessStorage(this.profileMe.roleFunctions[0].company);
    }
    this.auth.setUserMode('organizer');
    this.router.navigate(['/panel-control/events']);
  }

  /**
   * ===== Lo que movía el menú viejo =====
   *
   * El desplegable de "Mi perfil", las pestañas de mapas y borradores y el
   * guardado de notificaciones desde la propia pantalla del perfil. El menú
   * nuevo es plano y las notificaciones se guardan en su sección, así que
   * nada de esto se llama ya. Se queda comentado por si hay que recuperar la
   * navegación por pestañas.
   *
   * dropdownOpen / showUserMenu / openTab / titleTab / openedMapList /
   * openDraftsList eran los flags que lo sostenían.
   *
   * ngAfterViewInit() {
   *   if (this.router.url.indexOf('seat-maps') != -1) {
   *     this.openTab = true;
   *     this.titleTab = 'Mapas de asientos';
   *     this.openedMapList = true;
   *   }
   * }
   *
   * toggleDropdown(): void { this.dropdownOpen = !this.dropdownOpen; }
   * closeDropdown(): void { this.dropdownOpen = false; }
   * toggleMenu(): void { this.showUserMenu = !this.showUserMenu; }
   *
   * openMapList() {
   *   this.openTab = true;
   *   this.openedMapList = true;
   *   this.titleTab = 'Mapas de asientos';
   * }
   *
   * openListDrafts() {
   *   this.openTab = true;
   *   this.openDraftsList = true;
   *   this.titleTab = 'Eventos sin publicar';
   * }
   *
   * backTab() {
   *   this.openTab = false;
   *   this.openedMapList = false;
   *   this.openDraftsList = false;
   *   this.openedMapList ? this.router.navigate(['/profile']) : this.saveNotifications(false);
   * }
   *
   * saveNotifications(notifications: any) {
   *   this.openTab = false;
   *   if (notifications) {
   *     this.notifications = notifications;
   *     this.sendNotifications(notifications);
   *   }
   * }
   *
   * saveNewsLetter(event: any) {
   *   this.notifications.newsletter = event;
   *   this.sendNotifications(this.notifications);
   * }
   *
   * sendNotifications(notifications: any) {
   *   this.notificationsService.setNotifications(notifications.id, notifications).subscribe({
   *     next: () => { this.openInfoSave(); }
   *   });
   * }
   */

}
